import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alan,
  BosDurum,
  Buton,
  DurumRozeti,
  FiltreKarti,
  Ikon,
  Iskelet,
  Kart,
  MetinAlani,
  Rozet,
  SatirEylemi,
  Secim,
  Sekmeler,
  Tablo,
  cn,
  type UnkDurum,
} from "@koclukpanel/ui";
import { SayfaBasligiEki } from "./Kabuk";
import {
  ApiHatasi,
  destekAc,
  destekEkranIstatistigi,
  destekGetir,
  destekKapat,
  destekListesi,
  destekMesajEkle,
  ogrenciListesi,
  talebiKararaBagla,
  talepAc,
  talepKategorileri,
  talepleriGetir,
  type DestekDurumu,
  type DestekKonusu,
  type OgrenciTalebi,
  type TalepDurumu,
} from "./api";
import { DegisiklikTalepleri } from "./DegisiklikTalepleri";
import { TalepKategorileri } from "./KucukFormlar";

/**
 * TALEPLER — ogrenci/veli talebi (§5.8) ve destek talebi (§5.15)
 */

/**
 * `StudentRequest` durum adlari — 360 ekraninin Talepler sekmesi de
 * bunu kullaniyor. Disa aktarilmasinin sebebi dar: ayni tabloyu orada
 * yeniden yazmistim ve `IN_REVIEW` satirini ATLAMISTIM; ekranda ham
 * "IN_REVIEW" cikiyordu. Tek ev, tek tablo.
 */
export const TALEP_DURUM_ADI: Record<TalepDurumu, string> = {
  PENDING: "Bekliyor",
  IN_REVIEW: "İnceleniyor",
  APPROVED: "Onaylandı",
  REJECTED: "Reddedildi",
  CANCELLED: "İptal",
};

const DESTEK_DURUM_ADI: Record<DestekDurumu, string> = {
  OPEN: "Açık",
  IN_REVIEW: "İnceleniyor",
  ANSWERED: "Yanıtlandı",
  CLOSED: "Kapatıldı",
};

const KONU_ADI: Record<DestekKonusu, string> = {
  TECHNICAL: "Teknik sorun",
  HOW_TO: "Nasıl yapılır",
  BUG: "Hata bildirimi",
  SUGGESTION: "Öneri",
  OTHER: "Diğer",
};

/*
  UNİKOÇ "Taleplerim" durum renkleri (tarama-2 §3.11): Onaylandı yeşil, Reddedildi kırmızı;
  bekleyen turuncu. İptal için dördüncü renk yok → gri Rozet (renk uydurulmuyor).
*/
const TALEP_DURUM_RENGI: Partial<Record<TalepDurumu, UnkDurum>> = {
  PENDING: "bekliyor",
  IN_REVIEW: "planli",
  APPROVED: "tamamlandi",
  REJECTED: "eksik",
};

/** UNİKOÇ tablo zamanı: 31.03.2026 17:57. */
function kisaZaman(iso: string): string {
  return new Date(iso).toLocaleString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function zaman(iso: string): string {
  return new Date(iso).toLocaleString("tr-TR", {
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function Talepler({ acilanEkran }: { acilanEkran?: string }) {
  const [gorunum, setGorunum] = useState<"ogrenci" | "destek" | "degisiklik" | "kategori">(
    "ogrenci",
  );

  return (
    <div className="flex flex-col gap-4">
      <SayfaBasligiEki aciklama="Öğrenci, veli ve destek taleplerini görüntüleyin, karara bağlayın." />
      {/*
        SEKME DEĞİL, SÜZGEÇ DÜĞMELERİ — fark ekran okuyucuda.

        Elle yazılmış `role="tab"` şeridi vardı: `aria-selected` koyulmuş
        ama ok tuşlarıyla gezinme, gezinen odak ve `tabpanel` bağı YOKTU.
        Eksik kurulmuş bir ARIA deseni hiç yazmamaktan KÖTÜ — kullanıcıya
        "burada ok tuşlarıyla gezebilirsin" diye söz verip tutmuyor.

        `Sekmeler` `aria-pressed` kullanıyor: aynı listeyi süzen
        basılı-kalan düğmeler, ki gerçekte olan bu.
      */}
      <Sekmeler
        etiket="Talep görünümü"
        gorunum="hap"
        secili={gorunum}
        onSec={setGorunum}
        sekmeler={[
          { deger: "ogrenci", etiket: "Öğrenci/veli talepleri" },
          { deger: "destek", etiket: "Destek" },
          { deger: "degisiklik", etiket: "Program değişikliği" },
          { deger: "kategori", etiket: "Kategoriler" },
        ]}
      />

      {gorunum === "ogrenci" && <OgrenciTalepleri />}
      {gorunum === "destek" && <DestekTalepleri acilanEkran={acilanEkran} />}
      {gorunum === "degisiklik" && <DegisiklikTalepleri />}
      {gorunum === "kategori" && <TalepKategorileri />}
    </div>
  );
}

// ---------------------------------------------------------------
// OGRENCI / VELI TALEPLERI
// ---------------------------------------------------------------

function OgrenciTalepleri() {
  const [ogrenciId, setOgrenciId] = useState("");
  const [formAcik, setFormAcik] = useState(false);
  /*
    Kategori, durum ve tarih süzgeci EKRANDA — gelen listenin üstünde, yeni sorgu yok. Öğrenci
    süzgeci eskisi gibi sunucuya gidiyor (kapsam sunucuda). UNİKOÇ "Taleplerim" süzgeç kartı.
  */
  const [kategori, setKategori] = useState("");
  const [durum, setDurum] = useState<TalepDurumu | "">("");
  const [bas, setBas] = useState("");
  const [bit, setBit] = useState("");
  /*
    Açık satırlar. Varsayılan: BEKLEYEN talepler açık — karar bekleyen iş satırın arkasına
    saklanmasın; karara bağlanmışlar kapalı (UNİKOÇ'ta göz düğmesiyle açılıyor).
  */
  const [acik, setAcik] = useState<Record<string, boolean>>({});

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ["ogrenci-talepleri", ogrenciId],
    queryFn: () => talepleriGetir(undefined, ogrenciId || undefined),
  });
  // Öğrenci seçeneği kapsamdan gelir (sunucu süzer). Listeye izni olmayan
  // personelde (ör. finans) uç 403 verir: süzgeç ve form o zaman hiç çıkmaz.
  const ogrenciler = useQuery({
    queryKey: ["talep-ogrenci-secenekleri"],
    queryFn: () => ogrenciListesi(),
    retry: false,
  });
  const secenekler = ogrenciler.data?.ogrenciler ?? [];

  if (formAcik && secenekler.length > 0) {
    return <YeniOgrenciTalebi ogrenciler={secenekler} onKapat={() => setFormAcik(false)} />;
  }

  if (isPending) return <Iskelet satir={4} />;

  if (isError) {
    return (
      <BosDurum
        ton="hata"
        baslik="Talepler alınamadı"
        aciklama={error instanceof ApiHatasi ? error.message : undefined}
        eylem={
          <Buton gorunum="ikincil" onClick={() => void refetch()}>
            Tekrar dene
          </Buton>
        }
      />
    );
  }

  const kategoriler = [...new Set(data.talepler.map((t) => t.category.name))].sort((a, b) =>
    a.localeCompare(b, "tr"),
  );
  const gorunen = data.talepler.filter((t) => {
    if (kategori && t.category.name !== kategori) return false;
    if (durum && t.status !== durum) return false;
    const gun = t.createdAt.slice(0, 10);
    if (bas && gun < bas) return false;
    if (bit && gun > bit) return false;
    return true;
  });
  const acikMi = (t: OgrenciTalebi): boolean => acik[t.id] ?? t.status === "PENDING";

  return (
    <div className="flex flex-col gap-4">
      {/*
        "Yeni talep" UNİKOÇ'ta bandın sağında; burada gövdede, sağa yaslı — öğrenci listesine
        izni olmayanda hiç çizilmiyor ve band dışında (gömülü kullanım) kaybolmamalı.
      */}
      {secenekler.length > 0 && (
        <div className="flex justify-end">
          <Buton onClick={() => setFormAcik(true)}>
            <Ikon ad="ekle" className="size-4" />
            Öğrenci adına talep aç
          </Buton>
        </div>
      )}

      <FiltreKarti>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,1.4fr)_repeat(4,minmax(0,1fr))] lg:items-end">
          {secenekler.length > 0 && (
            <Secim
              etiket="Öğrenci"
              value={ogrenciId}
              onChange={(e) => setOgrenciId(e.target.value)}
            >
              <option value="">(Tümü)</option>
              {secenekler.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.ad} {o.soyad}
                </option>
              ))}
            </Secim>
          )}
          <Secim etiket="Kategori" value={kategori} onChange={(e) => setKategori(e.target.value)}>
            <option value="">(Tümü)</option>
            {kategoriler.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </Secim>
          <Secim
            etiket="Durum"
            value={durum}
            onChange={(e) => setDurum(e.target.value as TalepDurumu | "")}
          >
            <option value="">(Tümü)</option>
            {(Object.keys(TALEP_DURUM_ADI) as TalepDurumu[]).map((d) => (
              <option key={d} value={d}>
                {TALEP_DURUM_ADI[d]}
              </option>
            ))}
          </Secim>
          <Alan
            etiket="Başlangıç"
            type="date"
            value={bas}
            onChange={(e) => setBas(e.target.value)}
          />
          <Alan etiket="Bitiş" type="date" value={bit} onChange={(e) => setBit(e.target.value)} />
        </div>
      </FiltreKarti>

      {data.talepler.length === 0 ? (
        <BosDurum
          ikon="tamam"
          baslik="Talep yok"
          aciklama={
            ogrenciId
              ? "Bu öğrencinin kapsamınızda açık talebi bulunmuyor."
              : "Kapsamınızda açık talep bulunmuyor."
          }
        />
      ) : gorunen.length === 0 ? (
        <BosDurum
          baslik="Süzgece uyan talep yok"
          aciklama="Kategori, durum ya da tarihi temizleyin."
        />
      ) : (
        <Kart gorunum="unk" olarak="div" className="p-2 md:p-4">
          <Tablo
            satirlar={gorunen}
            anahtar={(t) => t.id}
            altSatir={(t) => (acikMi(t) ? <TalepDetayi talep={t} /> : null)}
            sutunlar={[
              {
                baslik: "Tarih",
                hucre: (t) => <span className="tabular-nums">{kisaZaman(t.createdAt)}</span>,
              },
              { baslik: "Kategori", hucre: (t) => t.category.name },
              {
                baslik: "Öğrenci",
                mobil: true,
                hucre: (t) =>
                  t.ogrenciAdi ? (
                    <span className="flex items-center gap-2">
                      <span
                        aria-hidden="true"
                        className="bg-mor-50 text-mor-700 grid size-7 shrink-0 place-items-center rounded-full text-xs font-bold"
                      >
                        {t.ogrenciAdi.charAt(0).toLocaleUpperCase("tr-TR")}
                      </span>
                      <p className="text-metin-koyu">{t.ogrenciAdi}</p>
                    </span>
                  ) : (
                    "—"
                  ),
              },
              {
                baslik: "Özet",
                mobil: true,
                hucre: (t) => (
                  <span className="flex flex-col">
                    <span className="text-metin-koyu font-semibold">{t.subject}</span>
                    {t.decisionNote && !acikMi(t) && (
                      <span className="text-metin-ikincil text-xs">
                        Karar notu: {t.decisionNote}
                      </span>
                    )}
                  </span>
                ),
              },
              {
                baslik: "Durum",
                mobil: true,
                hucre: (t) =>
                  TALEP_DURUM_RENGI[t.status] ? (
                    <DurumRozeti durum={TALEP_DURUM_RENGI[t.status] as UnkDurum}>
                      {TALEP_DURUM_ADI[t.status]}
                    </DurumRozeti>
                  ) : (
                    <Rozet>{TALEP_DURUM_ADI[t.status]}</Rozet>
                  ),
              },
              {
                baslik: "İşlem",
                mobil: true,
                etiketGizli: true,
                hucre: (t) => (
                  <SatirEylemi
                    tur="gor"
                    etiket={acikMi(t) ? `${t.subject} ayrıntısını kapat` : `${t.subject} ayrıntısı`}
                    aria-expanded={acikMi(t)}
                    onClick={() => setAcik((o) => ({ ...o, [t.id]: !acikMi(t) }))}
                  />
                ),
              },
            ]}
          />
          <p className="text-metin-ikincil px-2 pt-3 text-sm">
            {data.talepler.length} kayıttan {gorunen.length} kayıt gösteriliyor
          </p>
        </Kart>
      )}
    </div>
  );
}

/**
 * PERSONEL ÖĞRENCİ ADINA TALEP AÇAR — UNİKOÇ "Öğrenci Talebi Gönder"
 *
 * Öğrenci seçenekleri `/ogrenciler` kapsamından geliyor; yine de asıl
 * kapı sunucuda (`talepHedefi`): atanmış olmadığı öğrenci için istek 403
 * alır. Buradaki liste kolaylık, yetki değil.
 *
 * Kategori UNİKOÇ'taki gibi HAP düğmeleri (basılı-kalan, `aria-pressed`): kategori sayısı az
 * ve hepsini bir bakışta görmek, açılır listede aramaktan hızlı.
 */
function YeniOgrenciTalebi({
  ogrenciler,
  onKapat,
}: {
  ogrenciler: { id: string; ad: string; soyad: string }[];
  onKapat: () => void;
}) {
  const istemci = useQueryClient();
  const [ogrenciId, setOgrenciId] = useState("");
  const [kategoriId, setKategoriId] = useState("");
  const [konu, setKonu] = useState("");
  const [govde, setGovde] = useState("");

  const kategoriler = useQuery({ queryKey: ["talep-kategorileri"], queryFn: talepKategorileri });

  const mut = useMutation({
    mutationFn: () => talepAc({ ogrenciId, kategoriId, konu: konu.trim(), govde: govde.trim() }),
    onSuccess: async () => {
      await istemci.invalidateQueries({ queryKey: ["ogrenci-talepleri"] });
      onKapat();
    },
  });

  const temizle = (): void => {
    setOgrenciId("");
    setKategoriId("");
    setKonu("");
    setGovde("");
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <Buton onClick={onKapat}>
          <Ikon ad="gorevler" className="size-4" />
          Taleplerim
        </Buton>
      </div>
      <Kart gorunum="unk" olarak="section" aria-label="Öğrenci adına talep">
        <div className="flex flex-col gap-5">
          <div
            role="group"
            aria-labelledby="talep-kategori-basligi"
            className="flex flex-col gap-2"
          >
            <p id="talep-kategori-basligi" className="text-metin-koyu text-sm font-bold">
              Kategori
            </p>
            <div className="flex flex-wrap gap-2">
              {(kategoriler.data ?? []).map((k) => {
                const secili = kategoriId === k.id;
                return (
                  <button
                    key={k.id}
                    type="button"
                    aria-pressed={secili}
                    onClick={() => setKategoriId(k.id)}
                    className={cn(
                      "min-h-dokunma inline-flex items-center gap-2 rounded-full border px-4 text-sm font-semibold",
                      "ease-cikis transition-colors duration-(--sure-anlik)",
                      "focus-visible:outline-marka-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
                      secili
                        ? "border-mor-500 bg-mor-50 text-mor-700"
                        : "border-mor-kenar text-metin-koyu hover:bg-mor-50 border-dashed",
                    )}
                  >
                    <Ikon ad="tamam" className="size-4" />
                    {k.name}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <Secim
              etiket="Öğrenci"
              value={ogrenciId}
              onChange={(e) => setOgrenciId(e.target.value)}
              yardim="Yalnızca atandığınız öğrenciler için talep açabilirsiniz."
            >
              <option value="">Seçin</option>
              {ogrenciler.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.ad} {o.soyad}
                </option>
              ))}
            </Secim>
            <Alan
              etiket="Konu"
              value={konu}
              onChange={(e) => setKonu(e.target.value)}
              yardim="En az 3 karakter."
            />
          </div>
          <MetinAlani
            etiket="Açıklama"
            rows={5}
            value={govde}
            onChange={(e) => setGovde(e.target.value)}
            yardim="En az 10 karakter."
          />

          {mut.isError && (
            <p role="alert" className="text-hata-700 text-sm font-medium">
              {mut.error instanceof ApiHatasi ? mut.error.message : "Talep açılamadı."}
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            <Buton
              yukleniyor={mut.isPending}
              disabled={
                !ogrenciId || !kategoriId || konu.trim().length < 3 || govde.trim().length < 10
              }
              onClick={() => mut.mutate()}
            >
              Talebi gönder
            </Buton>
            <Buton gorunum="kesikli" onClick={temizle}>
              Temizle
            </Buton>
            <Buton gorunum="sessiz" onClick={onKapat}>
              Vazgeç
            </Buton>
          </div>
        </div>
      </Kart>
    </div>
  );
}

/**
 * ================================================================
 * KARAR DUGMESINI SUNUCUNUN CEVABI ACIYOR
 * ================================================================
 * Hangi iznin gerektigini KATEGORI soyluyor
 * (`RequestCategory.decisionPermission`) ve o izin roller arasinda
 * degisiyor: finans yalnizca odeme taleplerini karara baglar.
 *
 * Ekran bunu kendisi hesaplasaydi — kategori koduna bakip "bu mali
 * mi" diye karar verseydi — yetki mantigi iki yerde yasardi ve
 * yonetici yeni bir mali kategori ekledigi anda ekran yanlis dugme
 * gosterirdi. Sunucu `kararaBaglayabilirMiyim` doneriyor; ekran
 * yalnizca okuyor.
 *
 * Bu bir GUVENLIK siniri DEGIL: asil kontrol karar ucunda tekrar
 * yapiliyor. Burada amac, kullaniciya basamayacagi bir dugme
 * gostermemek.
 */
function TalepDetayi({ talep }: { talep: OgrenciTalebi }) {
  const istemci = useQueryClient();
  const [not, setNot] = useState("");

  const mut = useMutation({
    mutationFn: (hedef: "APPROVED" | "REJECTED") =>
      talebiKararaBagla(talep.id, hedef, not.trim() || null),
    onSuccess: async () => {
      setNot("");
      await istemci.invalidateQueries({ queryKey: ["ogrenci-talepleri"] });
    },
  });

  return (
    <div className="bg-mor-50/40 border-mor-kenar flex flex-col gap-3 rounded-2xl border p-4">
      <p className="text-metin-koyu text-sm whitespace-pre-wrap">{talep.body}</p>
      <p className="text-metin-ikincil text-xs">
        {talep.category.name} · {zaman(talep.createdAt)}
      </p>

      {talep.decisionNote && (
        <p className="rounded-alan bg-notr-0 text-notr-700 p-2 text-sm">
          <strong className="font-medium">Karar notu:</strong> {talep.decisionNote}
        </p>
      )}

      {talep.kararaBaglayabilirMiyim && (
        <div className="flex flex-col gap-2">
          <Alan
            etiket="Karar notu (isteğe bağlı)"
            value={not}
            onChange={(e) => setNot(e.target.value)}
            yardim="Reddedilen bir talebin gerekçesini görmeyen öğrenci aynı talebi tekrar açar."
          />
          <div className="flex gap-2">
            <Buton onClick={() => mut.mutate("APPROVED")} disabled={mut.isPending}>
              Onayla
            </Buton>
            <Buton gorunum="sessiz" onClick={() => mut.mutate("REJECTED")} disabled={mut.isPending}>
              Reddet
            </Buton>
          </div>
        </div>
      )}

      {/*
        Karar yetkisi olmayan ama listeyi goren kullanici (ornegin
        finans, akademik bir talepte) BOS bir ayrinti gormemeli: neden
        dugme olmadigini soylemek, "sistem bozuk" izleniminden iyi.
      */}
      {!talep.kararaBaglayabilirMiyim && talep.status === "PENDING" && (
        <p className="text-notr-600 text-xs">
          Bu kategorideki talepleri karara bağlama yetkiniz yok; yalnızca görüntülüyorsunuz.
        </p>
      )}

      {mut.isError && (
        <p role="alert" className="text-hata-500 text-sm">
          {mut.error instanceof ApiHatasi ? mut.error.message : "Talep karara bağlanamadı."}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------
// DESTEK
// ---------------------------------------------------------------

function DestekTalepleri({ acilanEkran }: { acilanEkran?: string }) {
  const [acik, setAcik] = useState<string | null>(null);
  const [yeniAcik, setYeniAcik] = useState(false);

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ["destek"],
    queryFn: () => destekListesi(),
  });

  if (yeniAcik) {
    return <YeniDestek acilanEkran={acilanEkran} onKapat={() => setYeniAcik(false)} />;
  }

  if (isPending) return <Iskelet satir={4} />;

  if (isError) {
    return (
      <BosDurum
        ton="hata"
        baslik="Destek talepleri alınamadı"
        aciklama={error instanceof ApiHatasi ? error.message : undefined}
        eylem={
          <Buton gorunum="ikincil" onClick={() => void refetch()}>
            Tekrar dene
          </Buton>
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {/*
        HANGI LISTEYI GORDUGU ACIKCA YAZIYOR.
        Ayni uc iki farkli liste donuyor (yanit izni olan hepsini,
        olmayan yalnizca kendininkini). Yazmasaydik yanit izni olmayan
        biri "destek talepleri bos, sistem calismiyor" diye dusunurdu.
      */}
      <p className="text-notr-600 text-xs">
        {data.yonetimGorunumu
          ? "Tüm destek talepleri gösteriliyor."
          : "Yalnızca kendi açtığınız talepler gösteriliyor."}
      </p>

      <div className="flex flex-wrap justify-end gap-2">
        <Buton onClick={() => setYeniAcik(true)}>Destek talebi aç</Buton>
      </div>

      {data.yonetimGorunumu && <EkranIstatistigi />}

      {data.talepler.length === 0 ? (
        <BosDurum
          ikon="tamam"
          baslik="Destek talebi yok"
          eylem={<Buton onClick={() => setYeniAcik(true)}>Destek talebi aç</Buton>}
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {data.talepler.map((t) => (
            <li key={t.id}>
              <button
                type="button"
                onClick={() => setAcik(acik === t.id ? null : t.id)}
                aria-expanded={acik === t.id}
                className={cn(
                  "min-h-dokunma rounded-alan flex w-full items-center gap-3 px-3 text-left",
                  "bg-notr-50 hover:bg-notr-100",
                  "focus-visible:outline-marka-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
                )}
              >
                <span className="text-notr-600 shrink-0 text-xs tabular-nums">#{t.ticketNo}</span>
                <span className="min-w-0 flex-1">
                  <span className="text-notr-900 block truncate text-sm font-medium">
                    {t.subject}
                  </span>
                  <span className="text-notr-600 block text-xs">
                    {KONU_ADI[t.topic]} · {zaman(t.createdAt)}
                    {t.screenKey ? ` · ${t.screenKey}` : ""}
                  </span>
                </span>
                <span className="bg-notr-0 text-notr-700 ring-notr-200 shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ring-1">
                  {DESTEK_DURUM_ADI[t.status]}
                </span>
              </button>

              {acik === t.id && (
                <DestekYazismasi destekId={t.id} yonetimGorunumu={data.yonetimGorunumu} />
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function DestekYazismasi({
  destekId,
  yonetimGorunumu,
}: {
  destekId: string;
  yonetimGorunumu: boolean;
}) {
  const istemci = useQueryClient();
  const [govde, setGovde] = useState("");

  const { data, isPending, isError, error } = useQuery({
    queryKey: ["destek", destekId],
    queryFn: () => destekGetir(destekId),
  });

  const mesajMut = useMutation({
    mutationFn: () => destekMesajEkle(destekId, govde.trim()),
    onSuccess: async () => {
      setGovde("");
      await istemci.invalidateQueries({ queryKey: ["destek", destekId] });
      await istemci.invalidateQueries({ queryKey: ["destek"] });
    },
  });

  const kapatMut = useMutation({
    mutationFn: () => destekKapat(destekId),
    onSuccess: async () => {
      await istemci.invalidateQueries({ queryKey: ["destek"] });
      await istemci.invalidateQueries({ queryKey: ["destek", destekId] });
    },
  });

  if (isPending) return <Iskelet satir={4} />;
  if (isError) {
    return (
      <p role="alert" className="text-hata-500 px-3 py-2 text-sm">
        {error instanceof ApiHatasi ? error.message : "Yazışma alınamadı."}
      </p>
    );
  }

  const kapali = data.status === "CLOSED";

  return (
    <Kart className="mt-2 flex flex-col gap-3">
      <ul className="flex flex-col gap-2">
        {data.mesajlar.map((m) => (
          <li
            key={m.id}
            className={cn(
              "rounded-alan p-2 text-sm",
              // `isStaff` SUNUCUDA izinden turetiliyor; istemciden
              // gelseydi ogrenci kendi mesajini "personel cevabi" gibi
              // isaretleyebilirdi.
              m.isStaff ? "bg-marka-50 text-notr-800" : "bg-notr-50 text-notr-700",
            )}
          >
            <span className="text-notr-600 block text-xs font-medium">
              {m.isStaff ? "Destek ekibi" : "Talebi açan"} · {zaman(m.createdAt)}
            </span>
            <span className="mt-0.5 block whitespace-pre-wrap">{m.body}</span>
          </li>
        ))}
        {data.mesajlar.length === 0 && <li className="text-notr-600 text-sm">Henüz mesaj yok.</li>}
      </ul>

      {kapali ? (
        <p className="text-notr-600 text-sm">
          Bu talep kapatıldı. Yeni bir sorun için yeni talep açın.
        </p>
      ) : (
        <form
          className="flex flex-col gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (govde.trim()) mesajMut.mutate();
          }}
        >
          <Alan etiket="Yanıt" value={govde} onChange={(e) => setGovde(e.target.value)} />
          <div className="flex gap-2">
            <Buton type="submit" disabled={!govde.trim() || mesajMut.isPending}>
              Gönder
            </Buton>
            {yonetimGorunumu && (
              <Buton
                gorunum="sessiz"
                type="button"
                onClick={() => kapatMut.mutate()}
                disabled={kapatMut.isPending}
              >
                Talebi kapat
              </Buton>
            )}
          </div>
        </form>
      )}

      {(mesajMut.isError || kapatMut.isError) && (
        <p role="alert" className="text-hata-500 text-sm">
          {(mesajMut.error ?? kapatMut.error) instanceof ApiHatasi
            ? ((mesajMut.error ?? kapatMut.error) as ApiHatasi).message
            : "İşlem tamamlanamadı."}
        </p>
      )}
    </Kart>
  );
}

/**
 * DESTEK TALEBİ AÇMA — matris §5.15
 *
 * `POST /yardim/destek` yazılıydı, hiçbir ekran çağırmıyordu: talepler
 * listeleniyor ama AÇILAMIYORDU. Yardım ekranında "aşağıdaki destek
 * formundan yazabilirsiniz" cümlesi vardı ve gösterdiği form yoktu.
 *
 * `ekranAnahtari` kullanıcının SON BULUNDUĞU ekran, bu ekran değil:
 * "en çok soru gelen ekranlar" raporunun anlamlı olması için sorunun
 * yaşandığı yerin yazılması gerekiyor.
 */
function YeniDestek({ acilanEkran, onKapat }: { acilanEkran?: string; onKapat: () => void }) {
  const istemci = useQueryClient();
  const [konu, setKonu] = useState<DestekKonusu>("HOW_TO");
  const [baslik, setBaslik] = useState("");
  const [mesaj, setMesaj] = useState("");
  const [acilanNo, setAcilanNo] = useState<number | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      destekAc({
        konu,
        baslik: baslik.trim(),
        mesaj: mesaj.trim(),
        ekranAnahtari: acilanEkran ?? null,
      }),
    onSuccess: async (c) => {
      setAcilanNo(c.ticketNo);
      await istemci.invalidateQueries({ queryKey: ["destek"] });
    },
  });

  if (acilanNo !== null) {
    return (
      <Kart baslik="Talebiniz açıldı">
        <div className="flex flex-col gap-3">
          <p className="text-notr-700 text-sm">
            Talep numaranız <strong>#{acilanNo}</strong>. Yanıt geldiğinde bildirim alacaksınız;
            yazışmayı listeden takip edebilirsiniz.
          </p>
          <div>
            <Buton onClick={onKapat}>Listeye dön</Buton>
          </div>
        </div>
      </Kart>
    );
  }

  return (
    <Kart
      baslik="Destek talebi"
      aciklama={
        acilanEkran
          ? `Talep "${acilanEkran}" ekranıyla ilişkilendirilecek.`
          : "Sorununuzu yazın; ekip yanıtlayacak."
      }
    >
      <div className="flex flex-col gap-4">
        <Secim etiket="Konu" value={konu} onChange={(e) => setKonu(e.target.value as DestekKonusu)}>
          {(Object.keys(KONU_ADI) as DestekKonusu[]).map((k) => (
            <option key={k} value={k}>
              {KONU_ADI[k]}
            </option>
          ))}
        </Secim>

        <Alan
          etiket="Başlık"
          value={baslik}
          onChange={(e) => setBaslik(e.target.value)}
          yardim="En az 3 karakter."
        />

        <MetinAlani
          etiket="Ne oldu?"
          rows={6}
          value={mesaj}
          onChange={(e) => setMesaj(e.target.value)}
          yardim="En az 10 karakter. Ne yapmaya çalıştığınızı ve ne gördüğünüzü yazarsanız daha hızlı çözülür."
        />

        {mut.isError && (
          <p role="alert" className="text-hata-700 text-sm font-medium">
            {mut.error instanceof ApiHatasi ? mut.error.message : "Talep açılamadı."}
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          <Buton
            yukleniyor={mut.isPending}
            disabled={baslik.trim().length < 3 || mesaj.trim().length < 10}
            onClick={() => mut.mutate()}
          >
            Talebi gönder
          </Buton>
          <Buton gorunum="ikincil" onClick={onKapat}>
            Vazgeç
          </Buton>
        </div>
      </div>
    </Kart>
  );
}

/**
 * EN ÇOK SORU GELEN EKRANLAR — matris §5.15
 *
 * Uç `yardim.destek.yanitla` istiyor; o yüzden yalnızca yönetim
 * görünümünde çiziliyor ve ancak AÇILDIĞINDA istek atıyor. Yetkisi
 * olmayana hiç sorulmuyor — 403'ü yakalayıp gizlemek, isteği hiç
 * atmamaktan kötü.
 */
function EkranIstatistigi() {
  const [acik, setAcik] = useState(false);
  const { data, isPending, isError } = useQuery({
    queryKey: ["destek-ekran-istatistigi"],
    queryFn: destekEkranIstatistigi,
    enabled: acik,
    retry: false,
  });

  if (!acik) {
    return (
      <div>
        <Buton gorunum="sessiz" onClick={() => setAcik(true)}>
          En çok soru gelen ekranlar
        </Buton>
      </div>
    );
  }

  return (
    <Kart baslik="En çok soru gelen ekranlar">
      {isPending ? (
        <Iskelet satir={3} />
      ) : isError ? (
        <p className="text-notr-600 text-sm">İstatistik alınamadı.</p>
      ) : data.length === 0 ? (
        <p className="text-notr-600 text-sm">Henüz hiçbir talep bir ekranla ilişkilendirilmemiş.</p>
      ) : (
        <ul className="flex flex-col gap-1">
          {data.map((r) => (
            <li key={r.ekran ?? "yok"} className="flex justify-between text-sm">
              <span className="text-notr-700">
                {/*
                  `null` ekran = talep yardım dışından, doğrudan açılmış.
                  "Bilinmeyen" yazmak yanlış olurdu: bilinmiyor değil,
                  bir ekrana bağlı DEĞİL.
                */}
                {r.ekran ?? <span className="text-notr-600">ekrana bağlı değil</span>}
              </span>
              <span className="text-notr-900 font-medium tabular-nums">{r.sayi}</span>
            </li>
          ))}
        </ul>
      )}
    </Kart>
  );
}
